import type {
    Breadcrumb,
    CategoryInterface,
    Maybe
} from '@pylot-data/pylotschema'
import { ProductInterface, CategoryTree, CmsPage } from '@pylot-data/fwrdschema'
import { getFirstNotEmpty } from '@corratech/pylot-utils'
import { TFunction } from 'react-i18next'
import type { PylotFrontendConfig } from 'config'
import { BlogBreadCrumbsDetails } from '@components/seo/src/Blog/BlogSchema'
import {
    SubCategories_Url as category_prefix,
    product_prefix
} from '@components/UrlConfiguration/UrlConfiguration'

type GetBreadcrumbs = {
    product?: Maybe<ProductInterface>
    cmsPage?: Maybe<CmsPage>
    category?: Maybe<CategoryInterface>
    pathname: string
    t: TFunction<string[]>
    titleOverride?: string
    config: PylotFrontendConfig
    blogBreadCrumbs?: BlogBreadCrumbsDetails
}

export type BreadcrumbItem = {
    '@type': string
    position: number
    name: string
    item?: string
}

export type ItemListElement = {
    '@type': string
    position: number
    name: string
    item?: string
}[]

export type BreadcrumbProduct = {
    '@context': string
    '@type': string
    itemListElement: ItemListElement
}

type BreadcrumbData = {
    name: string
    url_path: string
    breadcrumbs:
        | {
              category_id: string
              category_name: string
              category_uid: string
              category_url_path: string
          }[]
        | null
}

type CategoryWithChildren = CategoryInterface & {
    children?: Maybe<Array<Maybe<CategoryWithChildren>>>
}

const isCategoryWithChildren = (
    category?: Maybe<CategoryInterface>
): category is CategoryWithChildren => {
    return !!category && 'children' in category
}

const reduceCategories = (
    config: PylotFrontendConfig,
    suffix?: string | null,
    items?: Maybe<
        Array<Maybe<CategoryTree | BreadcrumbData | CategoryInterface>>
    >,
    product?: Maybe<ProductInterface>
): Array<BreadcrumbProduct> | undefined => {
    if (!items || !Array.isArray(items)) {
        return
    }
    const accumulate = { maxLength: 0, blockAccumulation: false }
    const result: Array<BreadcrumbProduct> = []
    items.forEach((category) => {
        if (!category) {
            return
        }
        const urlPath = category.url_path
        if (accumulate.blockAccumulation || !urlPath) {
            return accumulate
        }
        if (!category?.breadcrumbs) {
            result.push({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                    {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Homepage',
                        item: config.base.url.baseUrl
                    },
                    {
                        '@type': 'ListItem',
                        position: 2,
                        name: String(category?.name),
                        item: `${config.base.url.baseUrl}${
                            urlPath.length > 3 &&
                            urlPath?.substring(0, 2) === `${category_prefix}/`
                                ? ''
                                : `${category_prefix}/`
                        }${urlPath}${String(suffix)}`
                    },
                    {
                        '@type': 'ListItem',
                        position: 3,
                        name: String(product?.name),
                        item: `${config.base.url.baseUrl}${product_prefix}/${
                            product?.url_key
                        }${String(suffix)}`
                    }
                ]
            })
            return
        }

        const breadCrumbs: ItemListElement = []
        let position = 1
        const baseUrl = config.base.url.baseUrl
        const categoryPrefix = `${category_prefix}/`

        for (let i = 0; i < category.breadcrumbs.length; i++) {
            const breadcrumb = category.breadcrumbs[i]
            const categoryName = String(breadcrumb?.category_name)
            const categoryUrlPath = breadcrumb?.category_url_path

            const itemUrl =
                baseUrl + categoryPrefix + categoryUrlPath + String(suffix)

            position += 1
            breadCrumbs.push({
                '@type': 'ListItem',
                position,
                name: categoryName,
                item: itemUrl
            })

            if (i === category.breadcrumbs?.length - 1) {
                breadCrumbs.push({
                    '@type': 'ListItem',
                    position: position + 1,
                    name: String(category?.name),
                    item: `${config.base.url.baseUrl}${
                        urlPath.length > 3 &&
                        urlPath?.substring(0, 2) === `${category_prefix}/`
                            ? ''
                            : `${category_prefix}/`
                    }${urlPath}${String(suffix)}`
                })
            }
        }

        result.push({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Homepage',
                    item: config.base.url.baseUrl
                },
                ...breadCrumbs,
                {
                    '@type': 'ListItem',
                    position: breadCrumbs.length + 2,
                    name: String(product?.name),
                    item: `${config.base.url.baseUrl}${product_prefix}/${
                        product?.url_key
                    }${String(suffix)}`
                }
            ]
        })
    })
    return result
}

export const getBreadcrumbs = ({
    product,
    cmsPage,
    category,
    pathname,
    t,
    titleOverride,
    config,
    blogBreadCrumbs
}: GetBreadcrumbs): Array<BreadcrumbItem> | Array<BreadcrumbProduct> => {
    const { model, baseUrl } = config.base.url
    const breadcrumbItems: Array<BreadcrumbItem> = [
        {
            '@type': 'ListItem',
            position: 1,
            name: 'Homepage',
            item: baseUrl
        }
    ]

    let breadcrumbProduct: Array<BreadcrumbProduct> = [
        {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Homepage',
                    item: baseUrl
                }
            ]
        }
    ]

    const initCurrent = (name?: Maybe<string>, suffix?: string | null) => {
        breadcrumbItems.push({
            '@type': 'ListItem',
            position: breadcrumbItems.length + 1,
            name: String(name)
        })
    }

    const blogBreadcrumbItem = (name?: string, url?: string) => {
        breadcrumbItems.push({
            '@type': 'ListItem',
            position: breadcrumbItems.length + 1,
            name: String(name),
            item: url
        })
    }

    const setBlogNameBreadcrumbs = () => {
        if (blogBreadCrumbs?.name.length) {
            blogBreadCrumbs.name.forEach((breadCrumb) => {
                blogBreadcrumbItem(
                    breadCrumb,
                    `${baseUrl}${model.blog.directory}/${breadCrumb}`
                )
            })
        }
    }

    if (blogBreadCrumbs) {
        blogBreadcrumbItem('Blog', `${baseUrl}${model.blog.directory}`)
        setBlogNameBreadcrumbs()
        return breadcrumbItems
    }

    if (product) {
        const reduced = reduceCategories(
            config,
            model.category.suffix,
            product?.categories,
            product
        )
        if (reduced && reduced.length) breadcrumbProduct = reduced
    } else if (isCategoryWithChildren(category)) {
        const sortedCats = category?.breadcrumbs?.sort((a, b) => {
            return a?.category_level! - b?.category_level!
        })
        sortedCats?.map((item) => {
            breadcrumbItems.push({
                '@type': 'ListItem',
                position: breadcrumbItems.length + 1,
                name: String(item?.category_name),
                item: `${config.base.url.baseUrl}${category_prefix}/${
                    item?.category_url_path
                }${String(model.category.suffix)}`
            })
        })

        initCurrent(category?.name, model.category.suffix)
    } else if (pathname !== '' && (cmsPage || titleOverride)) {
        initCurrent(
            getFirstNotEmpty([titleOverride, cmsPage?.title]),
            model.cmsPage.suffix
        )
    }

    return product ? breadcrumbProduct : breadcrumbItems
}
